import { adminOverviewSchema } from "@rosc/contracts";
import { Button } from "@rosc/ui/components/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@rosc/ui/components/card";
import { Skeleton } from "@rosc/ui/components/skeleton";
import { useQuery } from "@tanstack/react-query";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from "chart.js";
import { Bar, Line } from "react-chartjs-2";
import { adminKeys } from "@/lib/admin-queries";
import { referenceClient } from "@/lib/orpc/client";
import { useAuthStore } from "@/store/auth.store";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Tooltip,
  Legend,
);

export function AdminOverview() {
  const uid = useAuthStore((state) => state.user?.uid);
  const query = useQuery({
    queryKey: adminKeys(uid ?? "").overview,
    queryFn: async ({ signal }) =>
      adminOverviewSchema.parse(
        await referenceClient.adminOverview(
          {},
          { signal, context: { expectedUserUid: uid } },
        ),
      ),
    enabled: !!uid,
    retry: false,
  });
  if (query.isPending)
    return (
      <Skeleton className="h-64 w-full" aria-label="Loading admin overview" />
    );
  if (query.isError)
    return (
      <div role="alert">
        Unable to load admin overview.{" "}
        <Button onClick={() => void query.refetch()}>Try again</Button>
      </div>
    );
  const data = query.data;
  const labels = data.daily.map((day) => day.date);
  const empty = data.daily.every(
    (day) =>
      day.signups === 0 && day.recordingMinutes === 0 && day.aiActions === 0,
  );
  return (
    <section className="space-y-6" aria-labelledby="admin-overview-title">
      <h2 id="admin-overview-title" className="text-xl font-semibold">
        Overview
      </h2>
      <p className="text-sm text-muted-foreground">
        Subscription counts use stored status and may briefly lag Stripe.
      </p>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {[
          { label: "New users, last 7 days", value: data.newUsers7d },
          { label: "New users, last 30 days", value: data.newUsers30d },
          { label: "Paying subscribers", value: data.paying },
          { label: "Free accounts", value: data.free },
          { label: "Active users, last 7 days", value: data.active7d },
        ].map(({ label, value }) => (
          <Card key={label} role="group" aria-label={label}>
            <CardHeader>
              <CardTitle>{label}</CardTitle>
            </CardHeader>
            <CardContent className="text-3xl font-semibold">
              {value.toLocaleString()}
            </CardContent>
          </Card>
        ))}
      </div>
      <p className="text-sm text-muted-foreground">
        Active users recorded in the last 7 days. Daily charts cover 30 days in
        America/Toronto.
      </p>
      {empty && <p role="status">No activity in the last 30 days.</p>}
      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Daily signups</CardTitle>
          </CardHeader>
          <CardContent className="h-72">
            <Line
              role="img"
              aria-label="Daily signups over the last 30 days"
              data={{
                labels,
                datasets: [
                  {
                    label: "Signups",
                    data: data.daily.map((day) => day.signups),
                    borderColor: "#0f766e",
                    backgroundColor: "#0f766e",
                  },
                ],
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
              }}
            />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Daily recording minutes and AI actions</CardTitle>
          </CardHeader>
          <CardContent className="h-72">
            <Bar
              role="img"
              aria-label="Daily recording minutes and AI actions over the last 30 days"
              data={{
                labels,
                datasets: [
                  {
                    label: "Recording minutes",
                    data: data.daily.map((day) => day.recordingMinutes),
                    backgroundColor: "#0f766e",
                    yAxisID: "minutes",
                  },
                  {
                    label: "AI actions",
                    data: data.daily.map((day) => day.aiActions),
                    backgroundColor: "#d97706",
                    yAxisID: "actions",
                  },
                ],
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                  minutes: {
                    beginAtZero: true,
                    position: "left",
                    title: { display: true, text: "Minutes" },
                  },
                  actions: {
                    beginAtZero: true,
                    position: "right",
                    title: { display: true, text: "AI actions" },
                    ticks: { precision: 0 },
                    grid: { drawOnChartArea: false },
                  },
                },
              }}
            />
          </CardContent>
        </Card>
      </div>
    </section>
  );
}
